// Mounted once in App.js (it replaces the old BadgeOverlayWrapper). Reads the
// member's unrevealed badge copies from GET /api/badges/me/unrevealed, polling
// every 60 s while the tab is visible and refetching when the member comes back
// to the tab or moves to another page, and hands them to BadgeRevealView. The
// view's callbacks become POST /me/collect, POST /me/share and navigation.
// The same response carries the nudges (a goal an action just moved): they show
// one at a time as a small toast, never while a card or cinematic is up, and each
// is marked seen (POST /me/nudges/seen) the moment it shows.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import {
  useCollectBadgeCopiesMutation,
  useGetUnrevealedBadgesQuery,
  useShareBadgeMutation,
} from "../../../services/rtkApi/badgesApi";
import BadgeNudgeToast from "../goals/BadgeNudgeToast";
import { useMarkBadgeNudgesSeenMutation } from "../goals/goalsApi";
import type { BadgeNudge, CollectBadgesRequest, UnrevealedCopy } from "../types";
import BadgeRevealView from "./BadgeRevealView";
import { useDocumentVisible, useFloatingChromeOpen, useOnReturn } from "./hooks";
import { collectionPath, planReveals, type RevealGroup } from "./revealQueue";

export const UNREVEALED_POLL_MS = 60_000;
// The backend checks badges about 5 s after the action that moved them (its
// queue debounces), so after a route change look once more a little later: a
// nudge or a fresh badge then shows right after the action, not a minute on.
export const LATE_LOOK_MS = 8_000;

/**
 * Pages where nothing pops up: the public share page (it is made to be
 * screenshotted), the chrome-less artwork pages, and the badge lab (it previews
 * the reveals with fake copies of its own).
 */
const QUIET_PATHS = ["/badge/s/", "/artworks/", "/badges-lab"];

const EMPTY: UnrevealedCopy[] = [];
const NO_NUDGES: BadgeNudge[] = [];

const atMs = (n: BadgeNudge) => Date.parse(n.at) || 0;

const BadgeRevealHost: React.FC = () => {
  const auth = useAppSelector((state) => state.auth);
  const signedIn = auth.bootstrapStatus === "ready" && Boolean(auth.token);
  const location = useLocation();
  const navigate = useNavigate();
  const visible = useDocumentVisible();
  const quiet = QUIET_PATHS.some((p) => location.pathname.startsWith(p));
  const chromeOpen = useFloatingChromeOpen();

  // an older backend without the endpoint answers 404: stop asking every minute
  const [endpointMissing, setEndpointMissing] = useState(false);
  const { data, error, refetch, isUninitialized } = useGetUnrevealedBadgesQuery(undefined, {
    skip: !signedIn,
    pollingInterval: signedIn && visible && !endpointMissing ? UNREVEALED_POLL_MS : 0,
  });
  useEffect(() => {
    if (error && "status" in error && error.status === 404) setEndpointMissing(true);
  }, [error]);
  const [collect] = useCollectBadgeCopiesMutation();
  const [share] = useShareBadgeMutation();
  const [markNudgesSeen] = useMarkBadgeNudgesSeenMutation();

  // copies the member dealt with in this session, hidden before the server confirms
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set());
  const markHandled = useCallback((ids: string[]) => {
    setHandled((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // the nudge on screen, and every nudge shown in this session: a refetch that
  // lands before the seen POST does must not show one twice
  const [nudge, setNudge] = useState<BadgeNudge | null>(null);
  const shownNudges = useRef<Set<string>>(new Set());

  // a different member signed in on this tab: forget the old one's handled copies and nudges
  const userKey = auth.userId || auth.username || "";
  useEffect(() => {
    setHandled(new Set());
    setNudge(null);
    shownNudges.current = new Set();
  }, [userKey]);

  const safeRefetch = useCallback(() => {
    if (!signedIn || isUninitialized) return;
    refetch();
  }, [signedIn, isUninitialized, refetch]);

  useOnReturn(safeRefetch, signedIn);

  const latestRefetch = useRef(safeRefetch);
  useEffect(() => {
    latestRefetch.current = safeRefetch;
  }, [safeRefetch]);
  const lateLook = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (lateLook.current) clearTimeout(lateLook.current);
    },
    []
  );

  // every route change is a chance a badge came in (a push tap lands on /badges)
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    safeRefetch();
    if (lateLook.current) clearTimeout(lateLook.current);
    lateLook.current = setTimeout(() => latestRefetch.current(), LATE_LOOK_MS);
    // the path is the trigger; safeRefetch changes with the query state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const items = useMemo(() => {
    const all = data?.items ?? EMPTY;
    return handled.size ? all.filter((i) => !handled.has(i.copyId)) : all;
  }, [data, handled]);

  const plan = useMemo(() => planReveals(items), [items]);
  // a card or cinematic is open or queued: no nudge shows over or between them
  const nudgeStashed = chromeOpen || plan.current !== null;

  const nudges = useMemo(() => {
    const all = (data?.nudges ?? NO_NUDGES).filter((n) => !!n && typeof n.id === "string" && !!n.next);
    return all.length > 1 ? [...all].sort((a, b) => atMs(a) - atMs(b)) : all;
  }, [data]);

  // one at a time, oldest first, marked seen as soon as it shows
  useEffect(() => {
    if (!signedIn || quiet || nudge || nudgeStashed) return;
    const next = nudges.find((n) => !shownNudges.current.has(n.id));
    if (!next) return;
    shownNudges.current.add(next.id);
    setNudge(next);
    markNudgesSeen({ ids: [next.id] })
      .unwrap()
      .catch((err: unknown) => {
        // it stays hidden for this session; the server drops it after 15 minutes
        console.warn("[badges] marking a nudge seen failed", err);
      });
  }, [signedIn, quiet, nudge, nudgeStashed, nudges, markNudgesSeen]);

  const dismissNudge = useCallback(() => setNudge(null), []);
  const openNudge = useCallback(
    (n: BadgeNudge) => {
      setNudge(null);
      navigate(collectionPath(n.next.code));
    },
    [navigate]
  );

  const send = useCallback(
    (body: CollectBadgesRequest) => {
      collect(body)
        .unwrap()
        .catch((err: unknown) => {
          // the copies stay hidden for this session and come back on the next load
          console.warn("[badges] collect failed", err);
        });
    },
    [collect]
  );

  const onCollect = useCallback(
    (copyIds: string[]) => {
      if (!copyIds.length) return;
      markHandled(copyIds);
      send({ copyIds });
    },
    [markHandled, send]
  );

  const onOpenBadge = useCallback(
    (code: string, copyIds: string[]) => {
      markHandled(copyIds);
      if (copyIds.length) send({ copyIds });
      navigate(collectionPath(code));
    },
    [markHandled, send, navigate]
  );

  const onShare = useCallback(
    async (group: RevealGroup) => {
      const res = await share({ code: group.code }).unwrap();
      markHandled(group.copyIds);
      send({ copyIds: group.copyIds });
      navigate(res.path || `/badge/s/${encodeURIComponent(res.shareId)}`);
    },
    [share, markHandled, send, navigate]
  );

  if (!signedIn || quiet || (!items.length && !nudge)) return null;

  const aboveDock = location.pathname === "/";
  return (
    <>
      {items.length > 0 && (
        <BadgeRevealView
          items={items}
          aboveDock={aboveDock}
          onCollect={onCollect}
          onOpenBadge={onOpenBadge}
          onShare={onShare}
        />
      )}
      {nudge && (
        <BadgeNudgeToast
          key={nudge.id}
          nudge={nudge}
          stashed={nudgeStashed}
          aboveDock={aboveDock}
          overTiles={plan.tiles.length > 0}
          onOpen={openNudge}
          onDone={dismissNudge}
        />
      )}
    </>
  );
};

export default BadgeRevealHost;
